/**
 * Simple Global Error Handler Plugin for Vue
 */

import ErrorHandler from '@/services/ErrorHandler';
import { ERROR_CODES, ERROR_CONFIG } from '@/config/errorCodes';

export default {
  install(Vue, { store, router }) {
    // Initialize error handler
    const errorHandler = new ErrorHandler(store, router);
    
    // Make error handler available globally
    Vue.prototype.$errorHandler = errorHandler;
    Vue.prototype.$handleError = (error, context) => errorHandler.handleError(error, context);
    
    // Global error codes and config
    Vue.prototype.$errorCodes = ERROR_CODES;
    Vue.prototype.$errorConfig = ERROR_CONFIG;
    
    // Simple error display methods
    Vue.prototype.$showError = (message, options = {}) => {
      store.dispatch('httpResponse/setError', {
        text: message,
        color: 'error',
        isActive: true,
        config: {
          autoHide: options.autoHide !== false,
          duration: options.duration || 5000
        }
      });
    };
    
    Vue.prototype.$showSuccess = (message, options = {}) => {
      store.dispatch('httpResponse/setError', {
        text: message,
        color: 'success',
        isActive: true,
        config: {
          autoHide: true,
          duration: options.duration || 3000
        }
      });
    };
    
    Vue.prototype.$showWarning = (message, options = {}) => {
      store.dispatch('httpResponse/setError', {
        text: message,
        color: 'warning',
        isActive: true,
        config: {
          autoHide: true,
          duration: options.duration || 4000
        }
      });
    };
    
    Vue.prototype.$showInfo = (message, options = {}) => {
      store.dispatch('httpResponse/setError', {
        text: message,
        color: 'info',
        isActive: true,
        config: {
          autoHide: true,
          duration: options.duration || 3000
        }
      });
    };
    
    // Clear errors
    Vue.prototype.$clearErrors = () => {
      store.dispatch('httpResponse/resetHttpResponse');
    };
    
    // Global Vue error handler
    Vue.config.errorHandler = (err, vm, info) => {
      console.error('Vue Error:', err, info);
      const error = {
        code: ERROR_CODES.UNKNOWN_ERROR,
        message: err.message || 'A Vue component error occurred',
        context: {
          component: vm?.$options.name || 'Unknown',
          info: info
        }
      };
      
      errorHandler.handleError(error);
    };
    
    // Mixin for component-level error handling
    Vue.mixin({
      methods: {
        // Component-level error handling
        handleError(error, context = {}) {
          const componentContext = {
            component: this.$options.name || 'Unknown',
            ...context
          };
          
          this.$handleError(error, componentContext);
        },
        
        // Component-level success message
        showSuccess(message, options = {}) {
          this.$showSuccess(message, options);
        },
        
        // Component-level warning message
        showWarning(message, options = {}) {
          this.$showWarning(message, options);
        },
        
        // Component-level info message
        showInfo(message, options = {}) {
          this.$showInfo(message, options);
        },
        
        // Component-level error message
        showError(message, options = {}) {
          this.$showError(message, options);
        }
      }
    });
  }
};